import { AHEmptyScreen, AHSeparator, Box, ListItem } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { FlatList } from "react-native";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { keyExtractorId } from "../../../../common/keyExtractors";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { WorkspaceRoutinesListItem } from "./WorkspaceRoutinesListItem";
import { WorkspaceRoutineItemRowContainer } from "./WorkspaceRoutineItemRowContainer";
import {
  workspaceRoutinesSort,
  WorkspaceRoutinesOrderBy,
} from "./workspaceRoutinesSort";
import { OrgSearchInput } from "../../OrgSearchInput";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { RoutineEditParams } from "../../../RoutineAdmin/RoutineEditMain/RoutineEditNavigation";
import { Image } from "expo-image";
import { useWorkspaceId } from "../../workspaceId";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { WorkspaceComplianceRoutinesList_workspace$key } from "./__generated__/WorkspaceComplianceRoutinesList_workspace.graphql";
import { WorkspaceComplianceRoutinesQuery } from "./__generated__/WorkspaceComplianceRoutinesQuery.graphql";
import { WorkspaceRoutinesTabScreenProps } from "../WorkspaceRoutinesNavigation";

function WorkspaceRoutinesSubtitle() {
  return (
    <FormattedMessage
      defaultMessage="Make sure nothing is overlooked by organising recurring tasks with daily, weekly or monthly checklists. Monitor progress by all units within your workspace in realtime."
      description="WorkspaceRoutinesSubtitle"
    />
  );
}

function List(props: {
  workspace: WorkspaceComplianceRoutinesList_workspace$key;
  openRoutineOccasion: (routineId: string, sequenceNumber: number) => void;
  openRoutineEdit: (routineId: string) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [order, setOrder] = React.useState<"ASC" | "DESC">("DESC");
  const [orderBy, setOrderBy] =
    React.useState<WorkspaceRoutinesOrderBy>("status");

  const data = useFragment(
    graphql`
      fragment WorkspaceComplianceRoutinesList_workspace on Workspace {
        id
        topics {
          __typename
        }
        routines {
          title
          orgTopic {
            name
          }
          inProgressOrNextWorkspaceOccasion(workspaceId: $workspaceId) {
            issueCount
            storesCount
            storesCompletedCount
          }
          currentSchedule {
            __typename
            ... on RoutineScheduleMonthly {
              endDay
              startDay
            }
            ... on RoutineScheduleWeekly {
              startWeekday
              endWeekday
            }
            ... on RoutineScheduleDaily {
              weekdays {
                startTime {
                  hour
                  minute
                }
                weekday
              }
            }
          }
          id
          ...WorkspaceRoutinesListItem_Routine
        }
      }
    `,
    props.workspace
  );

  const listStyle = useOrgListStyleProps();

  const listData = useMemo(
    () =>
      workspaceRoutinesSort({
        routines:
          data.routines?.filter((r) =>
            r.title.toLocaleLowerCase().includes(search.toLocaleLowerCase())
          ) ?? [],
        orderBy,
        order,
      }),
    [data.routines, order, orderBy, search]
  );

  const showTopics = data.topics ? data.topics.length > 0 : false;

  const intl_tokens = useIntlTokens();

  const ListHeaderComponent = useMemo(() => {
    return (
      <Box spaceTop="medium">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <ListItem disabled border="border-primary" displayMode="TABLE">
          <WorkspaceRoutineItemRowContainer
            title={
              <SortableColumnTitle
                title={intl_tokens.Name}
                onOrderBySelect={() => {
                  setOrderBy("title");
                }}
                order={orderBy === "title" ? order : null}
                onOrderChange={setOrder}
              />
            }
            freq={
              <SortableColumnTitle
                title={
                  <FormattedMessage
                    defaultMessage="Schedule"
                    description="WorkspaceRoutinesList schedule column"
                  />
                }
                onOrderBySelect={() => {
                  setOrderBy("freq");
                }}
                order={orderBy === "freq" ? order : null}
                onOrderChange={setOrder}
              />
            }
            issueCount={
              <SortableColumnTitle
                title={
                  <FormattedMessage
                    defaultMessage="Active Issues"
                    description="WorkspaceRoutinesList issueCount column"
                  />
                }
                onOrderBySelect={() => {
                  setOrderBy("issueCount");
                }}
                order={orderBy === "issueCount" ? order : null}
                onOrderChange={setOrder}
              />
            }
            status={
              <SortableColumnTitle
                title={
                  <FormattedMessage
                    defaultMessage="Status"
                    description="WorkspaceRoutinesList status column"
                  />
                }
                onOrderBySelect={() => {
                  setOrderBy("status");
                }}
                order={orderBy === "status" ? order : null}
                onOrderChange={setOrder}
              />
            }
            topic={
              showTopics ? (
                <SortableColumnTitle
                  title={intl_tokens.Topic}
                  onOrderBySelect={() => {
                    setOrderBy("topic");
                  }}
                  order={orderBy === "topic" ? order : null}
                  onOrderChange={setOrder}
                />
              ) : null
            }
            more={null}
          />
        </ListItem>
        <AHSeparator />
      </Box>
    );
  }, [search, intl_tokens.Name, intl_tokens.Topic, orderBy, order, showTopics]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <WorkspaceRoutinesListItem
          workspaceRoutine={item}
          isLast={index === listData.length - 1}
          openRoutineOccasion={props.openRoutineOccasion}
          openRoutineEdit={props.openRoutineEdit}
          showTopics={showTopics}
        />
      );
    },
    [
      listData.length,
      props.openRoutineOccasion,
      props.openRoutineEdit,
      showTopics,
    ]
  );

  if ((data.routines?.length ?? 0) === 0) {
    return (
      <AHEmptyScreen
        title={intl_tokens.CentralisedChecklists}
        subtitle={<WorkspaceRoutinesSubtitle />}
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./Checklist.png")}
            />
          </Box>
        }
      />
    );
  }

  return (
    <FlatList
      {...listStyle}
      ListEmptyComponent={<WorkspaceEmptyListItem />}
      ListHeaderComponent={ListHeaderComponent}
      stickyHeaderIndices={[0]}
      keyExtractor={keyExtractorId}
      data={listData}
      ItemSeparatorComponent={AHSeparator}
      renderItem={renderItem}
    />
  );
}

function InnerWorkspaceComplianceRoutines({
  fetchKey,
  openRoutineEdit,
  openRoutineOccasion,
}: {
  openRoutineEdit: (state: RoutineEditParams) => void;
  openRoutineOccasion: (routineId: string, sequenceNumber: number) => void;
  fetchKey: string;
}) {
  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<WorkspaceComplianceRoutinesQuery>(
    graphql`
      query WorkspaceComplianceRoutinesQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          id
          ...WorkspaceComplianceRoutinesList_workspace
        }
      }
    `,
    {
      workspaceId,
    },
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  return data.workspace.id ? (
    <List
      workspace={data.workspace}
      openRoutineOccasion={openRoutineOccasion}
      openRoutineEdit={(routineId) => {
        openRoutineEdit({
          orgWorkspaceId: data.workspace.id,
          id: routineId,
        });
      }}
    />
  ) : null;
}

export function WorkspaceComplianceRoutines(
  props: WorkspaceRoutinesTabScreenProps<"WorkspaceComplianceRoutines">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceComplianceRoutines
          openRoutineEdit={(params) => {
            props.navigation.navigate("RoutineEdit", {
              ...params,
            });
          }}
          openRoutineOccasion={(routineId, sequenceNumber) => {
            props.navigation.navigateDeprecated("WorkspaceRoutineOccasion", {
              routineId,
              sequenceNumber,
              archived: false,
            });
          }}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
